/* --------------------------------------------------------------------
   DS 418 Annex A helpers for R- and U-value computations – DEBUG EDITION
-------------------------------------------------------------------- */

/* eslint-disable no-console */

const ALPHA = 0.8;     // Annex A.3 fastener-coefficient

/* ── tiny helpers ────────────────────────────────────────────── */
const toNum = (v) => {
    if (v === null || v === undefined || v === '') return NaN;
    if (typeof v === 'number') return v;
    /* tolerate European “comma” decimals */
    return Number(String(v).replace(',', '.'));
};
const isPos = (n) => Number.isFinite(n) && n > 0;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

/* ====================================================================
   1.  Effective λ for inhomogeneous (stud + insulation) layers
==================================================================== */
export const effLambdaComposite = (l) => {

    if (!l || l.type !== 'composite') return null;

    const b = toNum(l.woodWidth_mm) / 1000;   // [m]
    const cc = toNum(l.ccDistance_mm) / 1000; // [m]
    if (!b || !cc || b >= cc) return null;     // invalid geometry

    const f = b / cc;                         // timber fraction
    const λwood = isPos(l.lambdaWood) ? +l.lambdaWood : 0.12;
    const λins = toNum(l.lambdaIns);

    const λeff = isPos(λins) ? f * λwood + (1 - f) * λins : null;
    return λeff;
};

/* ====================================================================
   2.  Composite-layer resistance
==================================================================== */
export function compositeR(layerLike) {

    /* ‣ modern multi-sublayer shape --------------------------------- */
    if (Array.isArray(layerLike?.sublayers) && layerLike.sublayers.length) {
        const λWglobal = toNum(layerLike.lambdaWood) || 0.12;
        const λIglobal = toNum(layerLike.lambdaIns) || 0.040;

        let Rsum = 0;
        for (const sl of layerLike.sublayers) {
            const t = toNum(sl.thickness_m);
            const w = toNum(sl.woodWidth_mm);
            const cc = toNum(sl.ccDistance_mm);
            if (!isPos(t) || !isPos(cc)) continue;

            const f = clamp01(w / cc);
            const λW = isPos(sl.lambdaWood) ? +sl.lambdaWood : λWglobal;
            const λI = isPos(sl.lambdaIns) ? +sl.lambdaIns : λIglobal;
            const λef = f * λW + (1 - f) * λI;

            if (isPos(λef)) Rsum += t / λef;
        }
        return Rsum;
    }

    /* ‣ legacy single-layer shape ----------------------------------- */
    const t = toNum(layerLike?.thickness_m);
    const λW = toNum(layerLike?.lambdaWood) || 0.12;
    const λI = toNum(layerLike?.lambdaIns);
    const w = toNum(layerLike?.woodWidth_mm);
    const cc = toNum(layerLike?.ccDistance_mm);

    if (!isPos(t) || !isPos(λI) || !isPos(cc)) return 0;

    const f = clamp01(w / cc);
    const λef = f * λW + (1 - f) * λI;
    const R = λef > 0 ? t / λef : 0;
    return R;
}

/* ====================================================================
   3.  Resistance of a single layer
==================================================================== */
export function layerR(layer) {
    if (!layer) return 0;

    /* A) catalogue/custom with fixed R (needs no thickness) ---------- */
    const isFixedCat = layer.kind === 'fixedR' && isPos(layer.rValue);
    const isFixedOwn = layer.type === 'customMaterial' && layer.mode === 'fixedR' && isPos(layer.customR);
    if (isFixedCat || isFixedOwn) {
        const Rfixed = isFixedCat ? +layer.rValue : +layer.customR;
        return Rfixed;
    }

    /* thickness mandatory from here --------------------------------- */
    const t = toNum(layer.thickness_m);
    if (!isPos(t)) {
        return 0;
    }

    /* B) ordinary λ-materials --------------------------------------- */
    const isλcat = layer.kind === 'lambda' && isPos(layer.lambda);
    const isλown = layer.type === 'customMaterial' && layer.mode !== 'fixedR' && isPos(layer.customLambda);
    if (isλcat || isλown) {
        const λ = isλcat ? +layer.lambda : +layer.customLambda;
        const Rλ = t / λ;
        return Rλ;
    }

    /* C) inhomogeneous composite ------------------------------------ */
    if (layer.type === 'composite') {
        const λeff = effLambdaComposite(layer);
        const Rcomp = isPos(λeff) ? t / λeff : 0;
        return Rcomp;
    }

    /* fallback ------------------------------------------------------ */
    return 0;
}

/* ====================================================================
   4.  Total U-value  (inc. Annex A add-ons)
==================================================================== */
export function calcUValue(layers, opts = {}) {
    const Rsi = toNum(opts.Rsi) || 0;
    const Rse = toNum(opts.Rse) || 0;

    const Rlayers = Array.isArray(layers) ? layers.reduce((s, l) => s + layerR(l), 0) : 0;

    const RT = Rsi + Rlayers + Rse;
    if (!isPos(RT)) return null;

    const Ubase = 1 / RT;

    /* ── A.2: possible air gaps ------------------------------------- */
    let ΔUstar = 0;
    if (opts.addA2 && isPos(opts.Ustar)) {
        const Ri = toNum(opts.Ri);
        const ratio = Ri > 0 ? (Ri / RT) : 0;
        ΔUstar = +opts.Ustar * (ratio ** 2);
        if (ΔUstar < 0.005) ΔUstar = 0;
    }

    /* ── A.3: mechanical fasteners ---------------------------------- */
    let ΔUb = 0;
    if (opts.addA3) {
        const λb = toNum(isPos(opts.lambdaFast) ? opts.lambdaFast : opts.lambdaFastMode);
        const n = toNum(opts.nFast);
        const d_mm = toNum(opts.dia);
        const d1 = toNum(opts.len);
        const R1 = toNum(opts.R1);

        if (isPos(λb) && isPos(n) && isPos(d_mm) && isPos(d1) && isPos(R1)) {
            const Ab = Math.PI * (d_mm ** 2) / 4e6;   // mm² → m²
            const ratio = R1 / RT;
            ΔUb = (ALPHA * λb * Ab * n / d1) * (ratio ** 2);
            if (ΔUb < 0.005) ΔUb = 0;
        }
    }

    const Utotal = Ubase + ΔUstar + ΔUb;
    return Utotal;
}
